// frontend/src/widgets/WeavingReportWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-22
// Updated:      2026-09-09 16:06 BST
//
// Description:
//   Weaving / Journey Analysis Widget
//
//   Features:
//     - Summary KPIs (counts per scenario)
//     - Detail table (plates, times, cameras)
//     - ANPR job selector when no fixed jobId prop is supplied
//     - Route filtering
//     - Scenario filtering
//     - Client-side date / time filtering
//     - Pairing editor modal for route thresholds and camera pairing setup
//     - Snapshot preview
//     - Launch report preview modal using current widget filters
//     - Cap on-screen table rendering to avoid browser lockups on large result sets
//
// Notes:
//     - Uses backend weaving report endpoints
//     - Uses camera pairing CRUD endpoints under /api/anpr/cameras/pairings
//     - Fully theme-aware
//     - Uses canonical axiosInstance
//     - Defensively normalises backend error payloads so the page cannot crash
//     - Does NOT call weaving endpoints until a valid job has been selected
//
// Mobile Responsiveness (2026-09-09):
//     - Preserves all report loading, filtering, pairing CRUD, snapshot and report-preview logic.
//     - ANPR job selection, route-pairing controls, KPIs, filters and report actions stack on mobile.
//     - Desktop retains the existing wide detail table.
//     - Mobile reflows the SAME detail-table rows into labelled cards; no duplicate row tree is mounted.
//     - Mobile shows one complete weaving record per internal vertical scroll/snap.
//     - Dynamic mobile sizing uses the proven Vision pattern:
//         restore previous host sizing;
//         clear temporary table/root sizing;
//         measure the natural first rendered weaving record;
//         size the record viewport to one complete record;
//         wait a second animation frame;
//         resize the first width-matching fixed-height mobile host.
//     - Pairing editor becomes a near-fullscreen mobile sheet; its two-column form collapses to one column.
//     - Interactive controls become touch-safe and long values/snapshot rows wrap safely.
//     - No desktop drag/drop, report or pairing interaction semantics are changed.
//
// Operational Notes:
//     - MAX_RENDER_ROWS remains 500.
//     - Existing API routes, payloads, date filtering and report parameter construction are unchanged.
//     - No performance/data-loading rewrite is mixed into this mobile pass.
//

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";
import ReportDisplayModal from "../components/ReportDisplayModal";

const MAX_RENDER_ROWS = 500;

const DEFAULT_PAIRING_FORM = {
  id: null,
  job_id: "",
  start_camera_device_id: "",
  end_camera_device_id: "",
  route_code: "",
  route_label: "",
  pairing_type: "weaving",
  start_label: "",
  end_label: "",
  distance_meters: "",
  min_travel_seconds: "60",
  weaving_max_seconds: "7200",
  max_travel_seconds: "21600",
  is_active: true,
  active_from: "",
  active_to: "",
  notes: "",
};

const REPORT_FIELDS = {
  detail: [
    "job_id",
    "route_code",
    "route_label",
    "plate",
    "scenario_code",
    "scenario_label",
    "weaving_confirmed",
    "start_time",
    "end_time",
    "travel_seconds",
    "travel_minutes",
    "start_camera_name",
    "end_camera_name",
    "distance_meters",
    "estimated_avg_speed_kph",
    "start_snapshot_url",
    "end_snapshot_url",
  ],
  movement: [
    "pairing_id",
    "job_id",
    "route_code",
    "route_label",
    "pairing_type",
    "scenario_code",
    "scenario_label",
    "weaving_confirmed",
    "plate_normalized",
    "start_label",
    "end_label",
    "start_time",
    "start_camera_name",
    "start_snapshot_url",
    "end_time",
    "end_camera_name",
    "end_snapshot_url",
    "travel_seconds",
    "travel_minutes",
    "distance_meters",
    "estimated_avg_speed_kph",
    "estimated_avg_speed_mph",
    "min_travel_seconds",
    "weaving_max_seconds",
    "max_travel_seconds",
    "notes",
  ],
  summary: [
    "job_id",
    "pairing_id",
    "route_code",
    "route_label",
    "pairing_type",
    "scenario_code",
    "scenario_label",
    "scenario_count",
    "avg_travel_seconds",
    "avg_travel_minutes",
    "avg_speed_kph",
    "avg_speed_mph",
    "min_speed_kph",
    "max_speed_kph",
    "first_event_at",
    "last_event_at",
  ],
};

const formatTime = (ts) => {
  if (!ts) return "-";
  try {
    return new Date(ts).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return String(ts);
  }
};

const formatNumber = (value, decimals = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n.toFixed(decimals);
};

const toInputDateTime = (value) => {
  if (!value) return "";
  try {
    const dt = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(
      dt.getHours()
    )}:${pad(dt.getMinutes())}`;
  } catch {
    return "";
  }
};

const parseClientFilterDate = (value) => {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const parseClientFilterTime = (value) => {
  const dt = parseClientFilterDate(value);
  return dt ? dt.getTime() : null;
};

const withRowSortKeys = (row) => {
  const startTimeMs = parseClientFilterTime(row?.start_time);
  return {
    ...row,
    __vx_start_time_ms: startTimeMs,
  };
};

const toReportDate = (value, fallbackDate = new Date()) => {
  try {
    const dt = value ? new Date(value) : fallbackDate;
    if (Number.isNaN(dt.getTime())) return fallbackDate.toISOString().slice(0, 10);
    return dt.toISOString().slice(0, 10);
  } catch {
    return fallbackDate.toISOString().slice(0, 10);
  }
};

const normaliseErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          return item.msg || item.message || JSON.stringify(item);
        }
        return String(item);
      })
      .filter(Boolean)
      .join(" | ");
  }

  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return "Request failed.";
    }
  }

  if (typeof err?.message === "string" && err.message.trim()) {
    return err.message;
  }

  return "Request failed.";
};

function KPI({ label, value, theme }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 140,
        padding: 12,
        borderRadius: 10,
        backgroundColor: theme?.primary_color || "#111827",
        color: "#fff",
        textAlign: "center",
        border: `1px solid ${theme?.accent_color || "#f97316"}`,
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.9 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

function PairingModal({
  open,
  onClose,
  onSave,
  onDeactivate,
  form,
  setForm,
  cameras,
  saving,
  deactivating,
  error,
  currentJobLabel,
}) {
  const { theme } = useTheme();

  if (!open) return null;

  const inputStyle = {
    width: "100%",
    minHeight: 44,
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    backgroundColor: "#fff",
    fontFamily: "var(--font-body)",
    fontSize: 14,
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    color: "#666",
    marginBottom: 6,
    fontFamily: "var(--font-body)",
  };

  const actionButtonStyle = {
    backgroundColor: theme?.button_action?.normal || "#000",
    color: "#fff",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    fontWeight: 700,
    fontFamily: "var(--font-body)",
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
  };

  const secondaryButtonStyle = {
    backgroundColor: "#fff",
    color: "#222",
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontWeight: 700,
    fontFamily: "var(--font-body)",
    cursor: "pointer",
  };

  const deactivateButtonStyle = {
    backgroundColor: "#fff4f4",
    color: "#a10000",
    padding: "10px 16px",
    borderRadius: 8,
    border: "1px solid #f0caca",
    fontWeight: 700,
    fontFamily: "var(--font-body)",
    cursor: deactivating ? "not-allowed" : "pointer",
    opacity: deactivating ? 0.7 : 1,
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 8,
      }}
      onClick={onClose}
    >
      <div
        className="vx-weaving-pairing-modal"
        style={{
          width: "min(1100px, calc(100vw - 16px))",
          maxHeight: "calc(100dvh - 16px)",
          overflowY: "auto",
          backgroundColor: "#fff",
          borderRadius: 14,
          boxShadow: "0 18px 50px rgba(0,0,0,0.2)",
          padding: 16,
          boxSizing: "border-box",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: theme?.accent_color || "#111827" }}>
            {form.id ? "Edit Route Pairing" : "Create Route Pairing"}
          </div>
          <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
            {currentJobLabel || "Selected ANPR job"}
          </div>
        </div>

        {error ? (
          <div
            style={{
              backgroundColor: "#fff4f4",
              border: "1px solid #f0caca",
              color: "#a10000",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: "0.95rem",
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div>
            <label style={labelStyle}>Route Code</label>
            <input
              value={form.route_code}
              onChange={(e) => setForm((prev) => ({ ...prev, route_code: e.target.value }))}
              style={inputStyle}
              placeholder="route_1"
            />
          </div>

          <div>
            <label style={labelStyle}>Route Label</label>
            <input
              value={form.route_label}
              onChange={(e) => setForm((prev) => ({ ...prev, route_label: e.target.value }))}
              style={inputStyle}
              placeholder="Route 1"
            />
          </div>

          <div>
            <label style={labelStyle}>Start Camera</label>
            <select
              value={form.start_camera_device_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, start_camera_device_id: e.target.value }))
              }
              style={inputStyle}
            >
              <option value="">Select start camera</option>
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.camera_name || `Camera ${camera.id}`} (#{camera.id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>End Camera</label>
            <select
              value={form.end_camera_device_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, end_camera_device_id: e.target.value }))
              }
              style={inputStyle}
            >
              <option value="">Select end camera</option>
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.camera_name || `Camera ${camera.id}`} (#{camera.id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Start Label</label>
            <input
              value={form.start_label}
              onChange={(e) => setForm((prev) => ({ ...prev, start_label: e.target.value }))}
              style={inputStyle}
              placeholder="Junction 1 Off-Ramp"
            />
          </div>

          <div>
            <label style={labelStyle}>End Label</label>
            <input
              value={form.end_label}
              onChange={(e) => setForm((prev) => ({ ...prev, end_label: e.target.value }))}
              style={inputStyle}
              placeholder="Junction 2 Rejoin"
            />
          </div>

          <div>
            <label style={labelStyle}>Pairing Type</label>
            <select
              value={form.pairing_type}
              onChange={(e) => setForm((prev) => ({ ...prev, pairing_type: e.target.value }))}
              style={inputStyle}
            >
              <option value="weaving">Weaving</option>
              <option value="corridor">Corridor</option>
              <option value="journey_time">Journey Time</option>
              <option value="general">General</option>
            </select>
          </div>

          <div>
            <label style={labelStyle}>Distance (meters)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.distance_meters}
              onChange={(e) => setForm((prev) => ({ ...prev, distance_meters: e.target.value }))}
              style={inputStyle}
              placeholder="Optional"
            />
          </div>

          <div>
            <label style={labelStyle}>Min Travel Seconds</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.min_travel_seconds}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, min_travel_seconds: e.target.value }))
              }
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Weaving Max Seconds</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.weaving_max_seconds}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, weaving_max_seconds: e.target.value }))
              }
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Max Travel Seconds</label>
            <input
              type="number"
              min="1"
              step="1"
              value={form.max_travel_seconds}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, max_travel_seconds: e.target.value }))
              }
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Active From</label>
            <input
              type="datetime-local"
              value={form.active_from}
              onChange={(e) => setForm((prev) => ({ ...prev, active_from: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Active To</label>
            <input
              type="datetime-local"
              value={form.active_to}
              onChange={(e) => setForm((prev) => ({ ...prev, active_to: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div className="lg:col-span-2">
            <label style={labelStyle}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              style={{
                ...inputStyle,
                minHeight: 90,
                resize: "vertical",
              }}
              placeholder="Optional route / threshold notes"
            />
          </div>

          <div className="flex min-h-[44px] items-center gap-2 lg:col-span-2">
            <input
              id="pairing_is_active"
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
            <label htmlFor="pairing_is_active" style={{ fontSize: 14, color: "#444" }}>
              Pairing is active
            </label>
          </div>
        </div>

        <div className="mt-[18px] flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-between">
          <div>
            {form.id ? (
              <button type="button" className="vx-weaving-mobile-button" onClick={onDeactivate} style={deactivateButtonStyle}>
                {deactivating ? "Deactivating..." : "Deactivate Pairing"}
              </button>
            ) : null}
          </div>

          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:gap-3">
            <button type="button" className="vx-weaving-mobile-button" onClick={onClose} style={secondaryButtonStyle}>
              Cancel
            </button>
            <button type="button" className="vx-weaving-mobile-button" onClick={onSave} style={actionButtonStyle}>
              {saving ? "Saving..." : form.id ? "Update Pairing" : "Create Pairing"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WeavingReportWidget({ jobId = null }) {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const contentRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstRecordRef = useRef(null);

  const [jobOptions, setJobOptions] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(jobId ? String(jobId) : "");

  const [cameraOptions, setCameraOptions] = useState([]);
  const [pairings, setPairings] = useState([]);

  const [summary, setSummary] = useState([]);
  const [rows, setRows] = useState([]);

  const [routeFilter, setRouteFilter] = useState("all");
  const [scenarioFilter, setScenarioFilter] = useState("all");

  const [clientDateFrom, setClientDateFrom] = useState("");
  const [clientDateTo, setClientDateTo] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingPairings, setLoadingPairings] = useState(false);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const [error, setError] = useState("");
  const [pairingError, setPairingError] = useState("");

  const [showPairingModal, setShowPairingModal] = useState(false);
  const [pairingForm, setPairingForm] = useState(DEFAULT_PAIRING_FORM);
  const [savingPairing, setSavingPairing] = useState(false);
  const [deactivatingPairing, setDeactivatingPairing] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportModalData, setReportModalData] = useState(null);
  const [reportModalFields, setReportModalFields] = useState([]);
  const [reportModalMeta, setReportModalMeta] = useState(null);

  const effectiveJobId = jobId || (selectedJobId ? Number(selectedJobId) : null);

  const loadJobOptions = async () => {
    if (jobId) return;

    setLoadingJobs(true);
    try {
      const res = await axiosInstance.get("/api/anpr/cameras/jobs/anpr-options");
      const options = Array.isArray(res.data) ? res.data : [];
      setJobOptions(options);

      if (!selectedJobId && options.length === 1) {
        setSelectedJobId(String(options[0].id));
      }
    } catch (err) {
      console.error("Failed to load ANPR job options", err);
      setJobOptions([]);
      setError(normaliseErrorMessage(err));
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadCameras = async () => {
    setLoadingCameras(true);
    try {
      const res = await axiosInstance.get("/api/anpr/cameras");
      setCameraOptions(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load cameras", err);
      setCameraOptions([]);
      setError(normaliseErrorMessage(err));
    } finally {
      setLoadingCameras(false);
    }
  };

  const loadPairings = async (resolvedJobId) => {
    if (!resolvedJobId || Number.isNaN(Number(resolvedJobId))) {
      setPairings([]);
      return;
    }

    setLoadingPairings(true);
    try {
      const res = await axiosInstance.get(
        `/api/anpr/cameras/pairings?job_id=${encodeURIComponent(resolvedJobId)}&include_inactive=true`
      );
      setPairings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load pairings", err);
      setPairings([]);
      setError(normaliseErrorMessage(err));
    } finally {
      setLoadingPairings(false);
    }
  };

  const fetchData = async (resolvedJobId) => {
    if (!resolvedJobId || Number.isNaN(Number(resolvedJobId))) {
      setSummary([]);
      setRows([]);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const qs = `?job_id=${encodeURIComponent(resolvedJobId)}`;

      const [summaryRes, detailRes] = await Promise.all([
        axiosInstance.get(`/api/reports/weaving/summary${qs}`),
        axiosInstance.get(`/api/reports/weaving/detail${qs}`),
      ]);

      const detailRows = Array.isArray(detailRes.data) ? detailRes.data : [];

      setSummary(Array.isArray(summaryRes.data) ? summaryRes.data : []);
      setRows(detailRows.map(withRowSortKeys));
    } catch (err) {
      console.error("Weaving report load failed", err);
      setSummary([]);
      setRows([]);
      setError(normaliseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobOptions();
    loadCameras();
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      setSelectedJobId(String(jobId));
    }
  }, [jobId]);

  useEffect(() => {
    setRouteFilter("all");
    setScenarioFilter("all");
    setClientDateFrom("");
    setClientDateTo("");
    fetchData(effectiveJobId);
    loadPairings(effectiveJobId);
  }, [effectiveJobId]);

  const routeOptions = useMemo(() => {
    const map = new Map();

    pairings.forEach((p) => {
      if (p?.route_code) {
        map.set(p.route_code, p.route_label || p.route_code);
      }
    });

    rows.forEach((r) => {
      if (r?.route_code && !map.has(r.route_code)) {
        map.set(r.route_code, r.route_label || r.route_code);
      }
    });

    return Array.from(map.entries()).map(([code, label]) => ({ code, label }));
  }, [rows, pairings]);

  const scenarioOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r?.scenario_code) {
        map.set(r.scenario_code, r.scenario_label || r.scenario_code);
      }
    });
    return Array.from(map.entries()).map(([code, label]) => ({ code, label }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const fromMs = parseClientFilterTime(clientDateFrom);
    const toMs = parseClientFilterTime(clientDateTo);

    return rows.filter((r) => {
      if (routeFilter !== "all" && r.route_code !== routeFilter) return false;
      if (scenarioFilter !== "all" && r.scenario_code !== scenarioFilter) return false;

      const startMs = r.__vx_start_time_ms;
      if (fromMs !== null && (startMs === null || startMs < fromMs)) return false;
      if (toMs !== null && (startMs === null || startMs > toMs)) return false;

      return true;
    });
  }, [rows, routeFilter, scenarioFilter, clientDateFrom, clientDateTo]);

  const visibleRows = useMemo(() => {
    return filteredRows.slice(0, MAX_RENDER_ROWS);
  }, [filteredRows]);

  const hiddenRenderedRowCount = Math.max(0, filteredRows.length - visibleRows.length);

  const filteredSummary = useMemo(() => {
    if (routeFilter === "all") return summary;
    return summary.filter((s) => s.route_code === routeFilter);
  }, [summary, routeFilter]);

  const getCount = (code) => {
    return filteredSummary
      .filter((s) => s.scenario_code === code)
      .reduce((acc, item) => acc + Number(item.scenario_count || 0), 0);
  };

  const currentJobLabel = useMemo(() => {
    if (!effectiveJobId) return "";
    const match = jobOptions.find((j) => Number(j.id) === Number(effectiveJobId));
    return match?.job_display || match?.job_ref || `Job #${effectiveJobId}`;
  }, [effectiveJobId, jobOptions]);

  const currentRoutePairing = useMemo(() => {
    if (routeFilter === "all") return null;
    return pairings.find((p) => p.route_code === routeFilter && p.is_active) || null;
  }, [pairings, routeFilter]);

  const reportParameters = useMemo(() => {
    const params = {};
    if (effectiveJobId) params.job_id = Number(effectiveJobId);
    if (routeFilter !== "all") params.route_code = routeFilter;
    if (scenarioFilter !== "all") params.scenario_code = scenarioFilter;
    if (currentRoutePairing?.id) params.pairing_id = Number(currentRoutePairing.id);
    return params;
  }, [effectiveJobId, routeFilter, scenarioFilter, currentRoutePairing]);

  const reportDateRange = useMemo(() => {
    const fallbackStart = new Date("2026-01-01");
    const fallbackEnd = new Date();

    return {
      start_date: toReportDate(clientDateFrom, fallbackStart),
      end_date: toReportDate(clientDateTo, fallbackEnd),
    };
  }, [clientDateFrom, clientDateTo]);

  const runReportPreview = async (reportType = "detail") => {
    if (!effectiveJobId) {
      setError("Select an ANPR job before launching a report.");
      return;
    }

    const fields = REPORT_FIELDS[reportType] || REPORT_FIELDS.detail;

    const reportName =
      reportType === "movement"
        ? "ANPR Weaving – Movement Detail"
        : reportType === "summary"
        ? "ANPR Weaving – Scenario Summary"
        : "ANPR Weaving – Detail";

    setReportLoading(true);
    setError("");

    try {
      const payload = {
        name: reportName,
        fields,
        start_date: reportDateRange.start_date,
        end_date: reportDateRange.end_date,
        available_to_clients: false,
        parameters: reportParameters,
      };

      const res = await axiosInstance.post("/api/reports/run", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setReportModalFields(fields);
      setReportModalData(res.data);
      setReportModalMeta({
        name: reportName,
        start_date: reportDateRange.start_date,
        end_date: reportDateRange.end_date,
        row_count: res?.data?.row_count,
        parameters: {
          job_id: effectiveJobId,
          job: currentJobLabel || `Job #${effectiveJobId}`,
          route_code: routeFilter !== "all" ? routeFilter : null,
          scenario_code: scenarioFilter !== "all" ? scenarioFilter : null,
          pairing_id: currentRoutePairing?.id || null,
        },
      });
      setShowReportModal(true);
    } catch (err) {
      console.error("Failed to launch weaving report preview", err);
      setError(normaliseErrorMessage(err));
    } finally {
      setReportLoading(false);
    }
  };

  const resetPairingFormForCreate = () => {
    setPairingForm({
      ...DEFAULT_PAIRING_FORM,
      job_id: effectiveJobId ? String(effectiveJobId) : "",
      active_from: "",
      active_to: "",
    });
  };

  const openCreatePairing = () => {
    resetPairingFormForCreate();
    setPairingError("");
    setShowPairingModal(true);
  };

  const openEditPairing = (pairing) => {
    if (!pairing) return;

    setPairingForm({
      id: pairing.id,
      job_id: String(pairing.job_id || effectiveJobId || ""),
      start_camera_device_id: String(pairing.start_camera_device_id || ""),
      end_camera_device_id: String(pairing.end_camera_device_id || ""),
      route_code: pairing.route_code || "",
      route_label: pairing.route_label || "",
      pairing_type: pairing.pairing_type || "weaving",
      start_label: pairing.start_label || "",
      end_label: pairing.end_label || "",
      distance_meters:
        pairing.distance_meters === null || pairing.distance_meters === undefined
          ? ""
          : String(pairing.distance_meters),
      min_travel_seconds: String(pairing.min_travel_seconds ?? 60),
      weaving_max_seconds: String(pairing.weaving_max_seconds ?? 7200),
      max_travel_seconds: String(pairing.max_travel_seconds ?? 21600),
      is_active: !!pairing.is_active,
      active_from: toInputDateTime(pairing.active_from),
      active_to: toInputDateTime(pairing.active_to),
      notes: pairing.notes || "",
    });

    setPairingError("");
    setShowPairingModal(true);
  };

  const savePairing = async () => {
    if (!effectiveJobId) {
      setPairingError("Select a job first.");
      return;
    }

    if (!pairingForm.route_code.trim()) {
      setPairingError("Route code is required.");
      return;
    }

    if (!pairingForm.route_label.trim()) {
      setPairingError("Route label is required.");
      return;
    }

    if (!pairingForm.start_camera_device_id || !pairingForm.end_camera_device_id) {
      setPairingError("Start camera and end camera are required.");
      return;
    }

    if (pairingForm.start_camera_device_id === pairingForm.end_camera_device_id) {
      setPairingError("Start camera and end camera must be different.");
      return;
    }

    const payload = {
      job_id: Number(effectiveJobId),
      start_camera_device_id: Number(pairingForm.start_camera_device_id),
      end_camera_device_id: Number(pairingForm.end_camera_device_id),
      route_code: pairingForm.route_code.trim(),
      route_label: pairingForm.route_label.trim(),
      pairing_type: pairingForm.pairing_type || "weaving",
      start_label: pairingForm.start_label.trim() || null,
      end_label: pairingForm.end_label.trim() || null,
      distance_meters: pairingForm.distance_meters === "" ? null : Number(pairingForm.distance_meters),
      min_travel_seconds: Number(pairingForm.min_travel_seconds || 0),
      weaving_max_seconds: Number(pairingForm.weaving_max_seconds || 0),
      max_travel_seconds: Number(pairingForm.max_travel_seconds || 0),
      is_active: !!pairingForm.is_active,
      active_from: pairingForm.active_from ? new Date(pairingForm.active_from).toISOString() : null,
      active_to: pairingForm.active_to ? new Date(pairingForm.active_to).toISOString() : null,
      notes: pairingForm.notes.trim() || null,
    };

    setSavingPairing(true);
    setPairingError("");

    try {
      if (pairingForm.id) {
        await axiosInstance.patch(`/api/anpr/cameras/pairings/${pairingForm.id}`, payload);
      } else {
        await axiosInstance.post("/api/anpr/cameras/pairings", payload);
      }

      setShowPairingModal(false);
      await loadPairings(effectiveJobId);
      await fetchData(effectiveJobId);
    } catch (err) {
      console.error("Failed to save pairing", err);
      setPairingError(normaliseErrorMessage(err));
    } finally {
      setSavingPairing(false);
    }
  };

  const deactivatePairing = async () => {
    if (!pairingForm.id) return;

    setDeactivatingPairing(true);
    setPairingError("");

    try {
      await axiosInstance.post(`/api/anpr/cameras/pairings/${pairingForm.id}/deactivate`);
      setShowPairingModal(false);
      await loadPairings(effectiveJobId);
      await fetchData(effectiveJobId);
    } catch (err) {
      console.error("Failed to deactivate pairing", err);
      setPairingError(normaliseErrorMessage(err));
    } finally {
      setDeactivatingPairing(false);
    }
  };

  const activePairingsForJob = useMemo(
    () => pairings.filter((p) => p.is_active),
    [pairings]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const content = contentRef.current;
    const viewport = recordsViewportRef.current;
    const firstRecord = firstRecordRef.current;

    if (!root || !content || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstRecord = firstRecordRef.current;

        if (currentFirstRecord) {
          const recordHeight = Math.ceil(
            Math.max(
              currentFirstRecord.getBoundingClientRect().height || 0,
              currentFirstRecord.scrollHeight || 0
            )
          );

          if (Number.isFinite(recordHeight) && recordHeight > 0) {
            const viewportHeight = recordHeight + 16;
            viewport.style.height = `${viewportHeight}px`;
            viewport.style.minHeight = `${viewportHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";
        content.style.height = "auto";
        content.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    resizeObserver?.observe(content);

    if (resizeObserver && firstRecord) {
      resizeObserver.observe(firstRecord);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
      content.style.height = "";
      content.style.minHeight = "";
    };
  }, [
    activePairingsForJob.length,
    clientDateFrom,
    clientDateTo,
    effectiveJobId,
    error,
    filteredRows.length,
    loading,
    routeFilter,
    scenarioFilter,
    selectedJobId,
    visibleRows.length,
  ]);

  return (
    <>
      <div
        ref={widgetRootRef}
        className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      >
        <style>{`
          @media (max-width: 767px) {
            .vx-weaving-mobile-button {
              width: 100% !important;
              min-height: 44px;
              white-space: normal !important;
            }

            .vx-weaving-table-viewport {
              overflow-y: auto !important;
              overflow-x: hidden !important;
              overscroll-behavior-y: contain;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: none;
              scroll-snap-type: y mandatory;
            }

            .vx-weaving-table-viewport::-webkit-scrollbar {
              display: none;
            }

            .vx-weaving-table {
              display: block;
              width: 100%;
            }

            .vx-weaving-table thead {
              display: none;
            }

            .vx-weaving-table tbody {
              display: grid;
              gap: 8px;
              padding: 8px;
              width: 100%;
              box-sizing: border-box;
            }

            .vx-weaving-data-row {
              display: grid;
              width: 100%;
              min-width: 0;
              overflow: hidden;
              border: 1px solid #e5e7eb !important;
              border-radius: 12px;
              background: #fff;
              scroll-snap-align: start;
              scroll-snap-stop: always;
            }

            .vx-weaving-data-row td {
              display: grid;
              grid-template-columns: minmax(102px, 0.42fr) minmax(0, 1fr);
              gap: 10px;
              align-items: start;
              width: 100%;
              min-width: 0;
              padding: 8px 10px !important;
              border-bottom: 1px solid #f3f4f6;
              white-space: normal !important;
              overflow-wrap: anywhere;
              box-sizing: border-box;
            }

            .vx-weaving-data-row td::before {
              color: #6b7280;
              font-size: 10px;
              line-height: 14px;
              font-weight: 900;
              text-transform: uppercase;
              letter-spacing: 0.2px;
            }

            .vx-weaving-data-row td:nth-child(1)::before { content: "Plate"; }
            .vx-weaving-data-row td:nth-child(2)::before { content: "Route"; }
            .vx-weaving-data-row td:nth-child(3)::before { content: "Scenario"; }
            .vx-weaving-data-row td:nth-child(4)::before { content: "Start"; }
            .vx-weaving-data-row td:nth-child(5)::before { content: "End"; }
            .vx-weaving-data-row td:nth-child(6)::before { content: "Time (min)"; }
            .vx-weaving-data-row td:nth-child(7)::before { content: "Speed (kph)"; }
            .vx-weaving-data-row td:nth-child(8)::before { content: "Start Camera"; }
            .vx-weaving-data-row td:nth-child(9)::before { content: "End Camera"; }
            .vx-weaving-data-row td:nth-child(10)::before { content: "Snapshots"; }

            .vx-weaving-empty-row {
              display: block;
              width: 100%;
            }

            .vx-weaving-empty-row td {
              display: block;
              width: 100%;
              box-sizing: border-box;
            }

            .vx-weaving-pairing-modal input,
            .vx-weaving-pairing-modal select,
            .vx-weaving-pairing-modal textarea {
              min-height: 44px;
            }
          }
        `}</style>

        <div
          ref={contentRef}
          className="flex h-auto min-h-0 flex-col gap-4 p-3 md:h-full md:p-4"
        >
        {error ? (
          <div
            style={{
              backgroundColor: "#fff4f4",
              border: "1px solid #f0caca",
              color: "#a10000",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: "0.95rem",
            }}
          >
            {String(error)}
          </div>
        ) : null}

        {!jobId ? (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(240px,420px)_minmax(0,1fr)] md:items-end">
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  color: "#666",
                  marginBottom: 6,
                  fontFamily: "var(--font-body)",
                }}
              >
                ANPR Job
              </label>
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                disabled={loadingJobs}
                style={{
                  width: "100%",
                  minHeight: 44,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#fff",
                  fontFamily: "var(--font-body)",
                }}
              >
                <option value="">
                  {loadingJobs ? "Loading jobs..." : "Select ANPR job"}
                </option>
                {jobOptions.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.job_display || `${job.job_ref || "Job"} (#${job.id})`}
                  </option>
                ))}
              </select>
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#666",
                paddingBottom: 2,
                fontFamily: "var(--font-body)",
              }}
            >
              {!selectedJobId
                ? "Choose a job to load weaving / route analysis."
                : currentJobLabel}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              backgroundColor: "#fff",
              padding: 12,
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 800,
                marginBottom: 8,
                color: theme?.accent_color || "#111827",
              }}
            >
              Active Route Pairings
            </div>

            {!effectiveJobId ? (
              <div style={{ color: "#666", fontSize: 13 }}>Select a job first.</div>
            ) : loadingPairings ? (
              <div style={{ color: "#666", fontSize: 13 }}>Loading pairings...</div>
            ) : activePairingsForJob.length === 0 ? (
              <div style={{ color: "#666", fontSize: 13 }}>
                No active route pairings exist for this job.
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {activePairingsForJob.map((pairing) => (
                  <button
                    key={pairing.id}
                    type="button"
                    className="min-h-[44px] max-w-full break-words"
                    onClick={() => {
                      setRouteFilter(pairing.route_code || "all");
                      openEditPairing(pairing);
                    }}
                    style={{
                      border: `1px solid ${
                        routeFilter === pairing.route_code
                          ? theme?.accent_color || "#f97316"
                          : "#d1d5db"
                      }`,
                      backgroundColor:
                        routeFilter === pairing.route_code ? "#fff7ed" : "#fff",
                      color: "#222",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 700,
                      fontFamily: "var(--font-body)",
                      cursor: "pointer",
                    }}
                    title={`${pairing.route_label || pairing.route_code} · ${
                      pairing.start_camera_name || pairing.start_camera_device_id
                    } → ${pairing.end_camera_name || pairing.end_camera_device_id}`}
                  >
                    {(pairing.route_label || pairing.route_code) ?? `Pairing ${pairing.id}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:w-auto lg:flex-wrap lg:justify-end lg:gap-2.5">
            <button
              type="button"
              className="vx-weaving-mobile-button"
              onClick={openCreatePairing}
              disabled={!effectiveJobId || loadingCameras}
              style={{
                backgroundColor: theme?.button_action?.normal || "#000",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                fontWeight: 700,
                fontFamily: "var(--font-body)",
                cursor: !effectiveJobId || loadingCameras ? "not-allowed" : "pointer",
                opacity: !effectiveJobId || loadingCameras ? 0.7 : 1,
              }}
            >
              Add Pairing
            </button>

            <button
              type="button"
              className="vx-weaving-mobile-button"
              onClick={() => openEditPairing(currentRoutePairing)}
              disabled={!currentRoutePairing}
              style={{
                backgroundColor: "#fff",
                color: "#222",
                padding: "10px 16px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontWeight: 700,
                fontFamily: "var(--font-body)",
                cursor: currentRoutePairing ? "pointer" : "not-allowed",
                opacity: currentRoutePairing ? 1 : 0.6,
              }}
            >
              Edit Current Route
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-4">
          <KPI label="Weaving" value={getCount("weaving")} theme={theme} />
          <KPI label="Traversal" value={getCount("route_traversal")} theme={theme} />
          <KPI label="Exited" value={getCount("exit_no_rejoin")} theme={theme} />
          <KPI label="Invalid" value={getCount("invalid_too_fast")} theme={theme} />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(5,minmax(160px,1fr))_auto] xl:items-end">
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontFamily: "var(--font-body)",
              }}
            >
              Route
            </label>
            <select
              value={routeFilter}
              onChange={(e) => setRouteFilter(e.target.value)}
              disabled={!effectiveJobId}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: "#fff",
                fontFamily: "var(--font-body)",
              }}
            >
              <option value="all">All Routes</option>
              {routeOptions.map((route) => (
                <option key={route.code} value={route.code}>
                  {route.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontFamily: "var(--font-body)",
              }}
            >
              Scenario
            </label>
            <select
              value={scenarioFilter}
              onChange={(e) => setScenarioFilter(e.target.value)}
              disabled={!effectiveJobId}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: "#fff",
                fontFamily: "var(--font-body)",
              }}
            >
              <option value="all">All Scenarios</option>
              {scenarioOptions.map((scenario) => (
                <option key={scenario.code} value={scenario.code}>
                  {scenario.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontFamily: "var(--font-body)",
              }}
            >
              Start Date / Time
            </label>
            <input
              type="datetime-local"
              value={clientDateFrom}
              onChange={(e) => setClientDateFrom(e.target.value)}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: "#fff",
                fontFamily: "var(--font-body)",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                color: "#666",
                marginBottom: 6,
                fontFamily: "var(--font-body)",
              }}
            >
              End Date / Time
            </label>
            <input
              type="datetime-local"
              value={clientDateTo}
              onChange={(e) => setClientDateTo(e.target.value)}
              style={{
                width: "100%",
                minHeight: 44,
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                backgroundColor: "#fff",
                fontFamily: "var(--font-body)",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="flex w-full gap-2">
            <button
              type="button"
              className="vx-weaving-mobile-button"
              onClick={() => {
                setClientDateFrom("");
                setClientDateTo("");
              }}
              style={{
                backgroundColor: "#fff",
                color: "#222",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                fontWeight: 700,
                fontFamily: "var(--font-body)",
                cursor: "pointer",
              }}
            >
              Clear Dates
            </button>
          </div>

          <div>
            <button
              type="button"
              className="vx-weaving-mobile-button"
              onClick={() => {
                fetchData(effectiveJobId);
                loadPairings(effectiveJobId);
              }}
              disabled={loading || !effectiveJobId}
              style={{
                backgroundColor: theme?.button_action?.normal || "#000",
                color: "#fff",
                padding: "10px 16px",
                borderRadius: 8,
                border: "none",
                fontWeight: 700,
                fontFamily: "var(--font-body)",
                cursor: loading || !effectiveJobId ? "not-allowed" : "pointer",
                opacity: loading || !effectiveJobId ? 0.7 : 1,
              }}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <button
            type="button"
            className="vx-weaving-mobile-button"
            onClick={() => runReportPreview("detail")}
            disabled={!effectiveJobId || reportLoading}
            style={{
              backgroundColor: theme?.button_action?.normal || "#000",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 8,
              border: "none",
              fontWeight: 700,
              fontFamily: "var(--font-body)",
              cursor: !effectiveJobId || reportLoading ? "not-allowed" : "pointer",
              opacity: !effectiveJobId || reportLoading ? 0.7 : 1,
            }}
          >
            {reportLoading ? "Opening..." : "Preview Detail Report"}
          </button>

          <button
            type="button"
            className="vx-weaving-mobile-button"
            onClick={() => runReportPreview("movement")}
            disabled={!effectiveJobId || reportLoading}
            style={{
              backgroundColor: "#fff",
              color: "#222",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontWeight: 700,
              fontFamily: "var(--font-body)",
              cursor: !effectiveJobId || reportLoading ? "not-allowed" : "pointer",
              opacity: !effectiveJobId || reportLoading ? 0.7 : 1,
            }}
          >
            Preview Movement Report
          </button>

          <button
            type="button"
            className="vx-weaving-mobile-button"
            onClick={() => runReportPreview("summary")}
            disabled={!effectiveJobId || reportLoading}
            style={{
              backgroundColor: "#fff",
              color: "#222",
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              fontWeight: 700,
              fontFamily: "var(--font-body)",
              cursor: !effectiveJobId || reportLoading ? "not-allowed" : "pointer",
              opacity: !effectiveJobId || reportLoading ? 0.7 : 1,
            }}
          >
            Preview Summary Report
          </button>
        </div>

        {hiddenRenderedRowCount > 0 ? (
          <div
            style={{
              backgroundColor: "#fff7ed",
              border: "1px solid #fdba74",
              color: "#9a3412",
              borderRadius: 8,
              padding: "10px 12px",
              fontSize: "0.9rem",
              fontWeight: 700,
            }}
          >
            Showing {visibleRows.length.toLocaleString("en-GB")} of {filteredRows.length.toLocaleString("en-GB")} filtered rows. Narrow the filters or launch the report preview/export for the full dataset.
          </div>
        ) : null}

        <div
          ref={recordsViewportRef}
          className="vx-weaving-table-viewport h-auto min-h-0 w-full overflow-visible rounded-[10px] border border-[#e5e7eb] bg-white md:flex-1 md:overflow-auto"
        >
          <table
            className="vx-weaving-table"
            style={{
              width: "100%",
              fontSize: 13,
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f7f7f7", textAlign: "left" }}>
                <th style={{ padding: "10px 12px" }}>Plate</th>
                <th style={{ padding: "10px 12px" }}>Route</th>
                <th style={{ padding: "10px 12px" }}>Scenario</th>
                <th style={{ padding: "10px 12px" }}>Start</th>
                <th style={{ padding: "10px 12px" }}>End</th>
                <th style={{ padding: "10px 12px" }}>Time (min)</th>
                <th style={{ padding: "10px 12px" }}>Speed (kph)</th>
                <th style={{ padding: "10px 12px" }}>Start Camera</th>
                <th style={{ padding: "10px 12px" }}>End Camera</th>
                <th style={{ padding: "10px 12px" }}>Snapshots</th>
              </tr>
            </thead>
            <tbody>
              {!effectiveJobId ? (
                <tr className="vx-weaving-empty-row">
                  <td colSpan={10} style={{ padding: "16px 12px", color: "#666" }}>
                    Select an ANPR job to view route analysis.
                  </td>
                </tr>
              ) : !loading && filteredRows.length === 0 ? (
                <tr className="vx-weaving-empty-row">
                  <td colSpan={10} style={{ padding: "16px 12px", color: "#666" }}>
                    No weaving data found for the current filter.
                  </td>
                </tr>
              ) : (
                visibleRows.map((r, i) => (
                  <tr
                    key={`${r.plate || r.plate_normalized || "row"}-${r.start_time || i}-${i}`}
                    ref={i === 0 ? firstRecordRef : null}
                    className="vx-weaving-data-row"
                    style={{ borderTop: "1px solid #efefef" }}
                  >
                    <td style={{ padding: "10px 12px", fontWeight: 700 }}>
                      {r.plate || r.plate_normalized || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {r.route_label || r.route_code || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {r.scenario_label || r.scenario_code || "-"}
                    </td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {formatTime(r.start_time)}
                    </td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      {formatTime(r.end_time)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {formatNumber(r.travel_minutes)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {formatNumber(r.estimated_avg_speed_kph)}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {r.start_camera_name || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {r.end_camera_name || "-"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {r.start_snapshot_url ? (
                          <img
                            src={r.start_snapshot_url}
                            alt="Start snapshot"
                            loading="lazy"
                            style={{
                              width: 60,
                              height: 34,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ddd",
                            }}
                          />
                        ) : null}
                        {r.end_snapshot_url ? (
                          <img
                            src={r.end_snapshot_url}
                            alt="End snapshot"
                            loading="lazy"
                            style={{
                              width: 60,
                              height: 34,
                              objectFit: "cover",
                              borderRadius: 6,
                              border: "1px solid #ddd",
                            }}
                          />
                        ) : null}
                        {!r.start_snapshot_url && !r.end_snapshot_url ? <span>-</span> : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>

      <PairingModal
        open={showPairingModal}
        onClose={() => {
          setShowPairingModal(false);
          setPairingError("");
        }}
        onSave={savePairing}
        onDeactivate={deactivatePairing}
        form={pairingForm}
        setForm={setPairingForm}
        cameras={cameraOptions}
        saving={savingPairing}
        deactivating={deactivatingPairing}
        error={pairingError}
        currentJobLabel={currentJobLabel}
      />

      {showReportModal && reportModalData && (
        <ReportDisplayModal
          onClose={() => setShowReportModal(false)}
          data={reportModalData.data}
          fields={reportModalFields}
          uid={reportModalData.timestamp_uid}
          meta={reportModalMeta || {}}
        />
      )}
    </>
  );
}
